import { Alert, Button } from '@mui/material';
import type { TFunction } from 'i18next';
import useLocalization from '../../../hooks/useLocalization';
import type { ZeroXDepositMinimumState } from '../../../schemas/zeroXDeposit';

type Props = {
  currency: string;
  minimum: ZeroXDepositMinimumState;
  onRetry: () => Promise<unknown>;
};

/** Helper text for the currency selector, matching the withdrawal minimum beneath its selector. */
export const getZeroXDepositMinimumHelp = (t: TFunction, currency: string, minimum: ZeroXDepositMinimumState) => {
  if (minimum.status === 'loading') {
    return t('zeroXMinimumLoading', { currency });
  }

  if (minimum.status !== 'available') {
    return undefined;
  }

  const labelKey = minimum.data.isEstimate ? 'zeroXMinimumEstimate' : 'zeroXMinimum';

  return t(labelKey, { amount: minimum.data.effectiveMinimum, currency });
};

/** A deposit below the minimum can be lost, so a missing minimum stays a prominent warning. */
const ZeroXDepositMinimum = ({ currency, minimum, onRetry }: Props) => {
  const { t } = useLocalization();

  if (minimum.status !== 'unavailable') {
    return null;
  }

  return (
    <Alert
      severity='warning'
      action={
        <Button variant='text' onClick={() => void onRetry()}>
          {t('retry')}
        </Button>
      }
    >
      {t('zeroXMinimumUnavailable', { currency })}
    </Alert>
  );
};

export default ZeroXDepositMinimum;
